/* eslint-disable no-await-in-loop */
import { PDFDocument, PDFPage, rgb, StandardFonts } from "pdf-lib"
import fontKit from "@pdf-lib/fontkit"
import Encoder from "code-128-encoder"
import QRCode from "qrcode"
import FileSaver from "file-saver"

import UploadApi from "../../services/api/Upload.api"
import { IBadgeImage, IBadgeText, IBadgeQRCode } from "../../interfaces/badges"
import { IUser } from "../../interfaces/user"
import UserEventApi from "../../services/api/UserEvent.api"
import BadgeApi from "../../services/api/Badge.api"

interface IFont {
  label: string
  value: string
  fontBytes?: any
  url?: string
}

const standardFonts = Object.entries(StandardFonts)

const urlCode128 = require("../../assets/fonts/code128.ttf")
const urlArial = require("../../assets/fonts/Arial.ttf")
const urlVerdana = require("../../assets/fonts/Verdana.ttf")

const fonts: IFont[] = [
  { label: "Code 128", value: "code128", url: urlCode128 },
  { label: "Arial", value: "arial", url: urlArial },
  { label: "Verdana", value: "verdana", url: urlVerdana },
  ...standardFonts.map(([p, v]) => ({
    label: p,
    value: p,
    fontBytes: v
  }))
]

const accessToProperty = (obj: any, properties: string) => {
  const arrayProperties = properties.split(".")
  return arrayProperties.reduce((prev, current) => {
    if (prev && prev[current]) return prev[current]
    return ""
  }, obj)
}

const hexToRgb = (hex: string) => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  const newHex = hex.replace(
    shorthandRegex,
    (m: string, r: string, g: string, b: string) => {
      return r + r + g + g + b + b
    }
  )

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(newHex)
  if (result)
    return rgb(
      parseInt(result[1], 16) / 256,
      parseInt(result[2], 16) / 256,
      parseInt(result[3], 16) / 256
    )
  return rgb(0, 0, 0)
}

const addImage = async (doc: PDFDocument, page: PDFPage, e: IBadgeImage) => {
  if (e.uuid) {
    e.imageBytes = await new UploadApi().createUrlToGet({
      bucket: "gayakoa-files",
      directory: "badge-images",
      fileName: e.uuid
    })
  }

  if (!e.imageBytes) return null

  return (e.imageType === "image/png"
    ? doc.embedPng(e.imageBytes)
    : doc.embedJpg(e.imageBytes)
  ).then(image => {
    page.drawImage(image, {
      x: e.x,
      y: e.y,
      width: e.width,
      height: e.height
    })
  })
}

const addText = async (
  doc: PDFDocument,
  page: PDFPage,
  e: IBadgeText,
  user: IUser
) => {
  const value = accessToProperty(user, e.properties)
  const content =
    e.fontFamily === "code128" ? new Encoder().encode(value) : value

  const find = fonts.find(f => f.value === e.fontFamily)

  if (!find) return null

  const bytes =
    find.fontBytes ||
    (await fetch(find.url || "").then(res => res.arrayBuffer()))

  return doc.embedFont(bytes).then(font => {
    let textHeight = font.heightAtSize(e.fontSize)
    let textWidth = font.widthOfTextAtSize(content, e.fontSize)
    let { fontSize } = e
    let { x } = e

    while (textWidth > e.maxWidth) {
      fontSize -= 1
      textWidth = font.widthOfTextAtSize(content, fontSize)
      textHeight = font.heightAtSize(fontSize)
    }

    if (e.textAlign === "center") {
      x = (e.maxWidth - textWidth) / 2 + x
    } else if (e.textAlign === "right") {
      x = e.maxWidth - (e.maxWidth - textWidth)
    }

    page.drawText(content, {
      x,
      y: e.y,
      size: fontSize,
      font,
      color: hexToRgb(e.color)
    })

    if (e.withBorder)
      page.drawRectangle({
        x: e.x - 5,
        y: e.y - 5,
        width: e.maxWidth + 10,
        height: textHeight + 10,
        borderColor: rgb(0, 0, 0),
        borderWidth: 1
      })
  })
}

const addQRCode = async (
  doc: PDFDocument,
  page: PDFPage,
  e: IBadgeQRCode,
  user: any
) => {
  const value = accessToProperty(user, "UserEvent.code")
  return new Promise((resolve, reject) =>
    QRCode.toDataURL(
      value,
      {
        width: e.width,
        scale: 2,
        margin: 0
      },
      (error, url) => {
        if (error) reject(error)
        doc.embedPng(url).then(image => {
          page.drawImage(image, {
            x: e.x,
            y: e.y
          })
          resolve()
        })
      }
    )
  )
}

export const downloadBadge = async (userId: number, badgeId: number) => {
  if (!userId || !badgeId) return null

  const [user, badge] = await Promise.all([
    new UserEventApi().getByUserId(userId),
    new BadgeApi().getOne(badgeId)
  ])

  const { settings, elements } = badge
  return PDFDocument.create().then(async doc => {
    doc.registerFontkit(fontKit)

    const page = doc.addPage([settings.width, settings.height])

    for (let i = 0; i < elements.length; i++) {
      const e = elements[i]
      e.withBorder = false
      if (e.type === "image") await addImage(doc, page, e)
      else if (e.type === "text") await addText(doc, page, e, user)
      else if (e.type === "qrcode") await addQRCode(doc, page, e, user)
    }

    const data = await doc.save()
    const blob = new Blob([data])
    FileSaver.saveAs(
      blob,
      `${user.firstName}-${user.lastName}-${user.UserEvent?.code}.pdf`
    )
  })
}
